import { io, Socket } from "socket.io-client";
import {
  getBaseUrl,
  Microservice,
} from "@funded-here-interface/common/src/constant/baseUrl";
import { Store } from "@reduxjs/toolkit";
import { Persistor } from "redux-persist";
import { QueryClient } from "react-query";
import { showSessionKickedOverlay } from "../Utils/sessionKickedOverlay";
import {
  SESSION_INVALIDATED_MESSAGE,
  SESSION_INVALIDATED_BY_LOGOUT_CODE,
} from "../constant/sessionConstants";
import { performStateCleanup } from "./standardizedLogout";
import {
  claimSessionInvalidation,
  resetSessionInvalidation,
} from "../Utils/sessionState";

let socket: Socket | null = null;

interface ConnectSessionSocketParams {
  store: Store;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tokenSelector: (state: any) => { token: string };
  resetActions?: Array<() => { type: string }>;
  persistor?: Persistor;
  queryClient?: QueryClient;
}

/**
 * Connects to the /session WebSocket namespace.
 * Listens for "session-invalidated" events and performs logout cleanup.
 * Uses a dynamic auth callback so reconnections always use the latest token.
 */
export const connectSessionSocket = ({
  store,
  tokenSelector,
  resetActions,
  persistor,
  queryClient,
}: ConnectSessionSocketParams): void => {
  // Disconnect existing socket before creating a new one
  disconnectSessionSocket();

  const baseUrl = getBaseUrl(Microservice.TRADE_DIRECTORY);
  const url = `${baseUrl}/session`;

  socket = io(url, {
    path: "/trade-directory/socket.io",
    auth: (cb: (data: { token: string }) => void) => {
      const { token } = tokenSelector(store.getState());
      cb({ token });
    },
    transports: ["websocket"],
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 3000,
  });

  socket.on("session-invalidated", async (data: { code?: string }) => {
    // Try to claim the shared lock — if interceptor already claimed it, do nothing
    if (!claimSessionInvalidation()) return;

    // Disconnect socket before cleanup
    disconnectSessionSocket();

    // Wipe local state clean immediately (so refresh won't restore the session)
    await performStateCleanup({ store, resetActions, persistor, queryClient });

    // Logout from another tab — silently redirect
    if (data?.code === SESSION_INVALIDATED_BY_LOGOUT_CODE) {
      resetSessionInvalidation();
      window.location.href = "/login";
      return;
    }

    // New login from another device — show overlay
    showSessionKickedOverlay(SESSION_INVALIDATED_MESSAGE, () => {
      resetSessionInvalidation();
      window.location.href = "/login";
    });
  });
};

/**
 * Disconnects the session socket if connected.
 */
export const disconnectSessionSocket = (): void => {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
};
